import ErrorHandler from '@/components/Error/ErrorHandler'
import FaturasView from '@/components/Fatura/FaturasView'
import SaldoAntecipadoResumoCard from '@/components/Fatura/SaldoAntecipadoResumoCard'
import Footer from '@/components/Footer'
import Loading from '@/components/Loading'
import Logo from '@/components/Logo'
import useAuth from '@/contexts/useAuth'
import useEmpresa from '@/contexts/useEmpresa'
import { getFaturasAbertas } from '@/services/fatura'
import { getSaldoAntecipado } from '@/services/saldo-antecipado'
import type { Fatura } from '@/types/fatura'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router-dom'

export default function GuestFaturas() {
    const { clienteHash, dataLimite: dataLimiteParam } =
        useParams<{ clienteHash: string; dataLimite?: string }>()
    const [searchParams] = useSearchParams()
    const searchDataLimite = searchParams.get('data') ?? undefined

    const { user } = useAuth()
    const { empresa } = useEmpresa()
    const { t } = useTranslation()

    const [faturas, setFaturas] = useState<Fatura[]>([])
    const [dataLimite, setDataLimite] = useState<string>()
    const [saldoDisponivel, setSaldoDisponivel] = useState<number | null>(null)
    const [temExtratoSaldo, setTemExtratoSaldo] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const effectiveDataLimite = dataLimiteParam ?? searchDataLimite
    const clienteId = user?.CdCliente
    const empresaId = empresa?.CdEmpresa

    const loadFaturas = useCallback(async () => {
        if (!clienteId || !empresaId) return

        setIsLoading(true)
        try {
            const [{ faturas, dataLimite }, saldo] = await Promise.all([
                getFaturasAbertas(effectiveDataLimite, clienteHash),
                getSaldoAntecipado(clienteHash).catch(() => null),
            ])
            setFaturas(faturas)
            setDataLimite(dataLimite)
            if (saldo) {
                setSaldoDisponivel(saldo.disponivel)
                setTemExtratoSaldo(saldo.extrato.length > 0)
            } else {
                setSaldoDisponivel(null)
                setTemExtratoSaldo(false)
            }
        } catch {
            setError('Ocorreu um erro ao carregar as faturas')
        } finally {
            setIsLoading(false)
        }
    }, [clienteHash, effectiveDataLimite, empresaId, clienteId])

    useEffect(() => {
        loadFaturas()
    }, [loadFaturas])

    if (error) return <ErrorHandler error={error} />
    if (!user || !empresa) return <Loading type="primary" />

    const mostrarSaldo =
        saldoDisponivel !== null && (saldoDisponivel > 0 || temExtratoSaldo)

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <header>
                <div className="nav-container">
                    <Logo className="logo" />
                </div>
                <div className="right">
                    <div className="hello">
                        <span>{t('Olá')}, </span>
                        <strong>{user.Nome?.split(' ')[0]}</strong>
                    </div>
                </div>
            </header>

            <main style={{ padding: 12, flex: 1 }}>
                {clienteHash && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                        <Link className="btn btn-outline-primary btn-sm" to={`/c/${clienteHash}/faturas/pagas`}>
                            {t('Faturas pagas')}
                        </Link>
                    </div>
                )}

                {mostrarSaldo && clienteHash ? (
                    <SaldoAntecipadoResumoCard
                        disponivel={saldoDisponivel}
                        to={`/c/${clienteHash}/faturas/saldo-antecipado`}
                    />
                ) : null}

                <FaturasView
                    faturas={faturas}
                    isLoading={isLoading}
                    onRefresh={loadFaturas}
                    clienteHash={clienteHash}
                    dataLimite={dataLimite}
                />
            </main>

            <Footer />
        </div>
    )
}
